import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { ConsentPanel, CookieBanner } from '@azoya-spa/ui'
import '@azoya-spa/ui/styles.css'
import { useConsent } from './ConsentContext'

// Les descriptions doivent rester compréhensibles par la clientèle du spa, pas
// par un juriste : ce que le traceur fait, qui le pose, ce qu'on en tire. Le
// détail réglementaire (durées, hébergeur) vit à l'article 7 des CGU, vers
// lequel le panneau renvoie.
const PURPOSES = [
  {
    id: 'necessary',
    label: 'Strictement nécessaires',
    description:
      'Indispensables au fonctionnement du site et au module de réservation en ligne Planity. Ils ne peuvent pas être désactivés.',
    required: true,
  },
  {
    id: 'analytics',
    label: "Mesure d'audience",
    description:
      "Nous indiquent quelles pages sont consultées et sur quels liens vous cliquez — notamment la réservation, les cartes cadeaux, les familles de soins consultées et les moyens de contact — afin d'améliorer le site. Mesure hébergée dans l'Union européenne, sans publicité ni revente de données.",
  },
]

// Dérivés de PURPOSES plutôt qu'écrits en dur : ajouter une finalité sans
// penser à ces deux objets donnerait un « Tout accepter » qui n'accepte pas
// tout, ou pire, un « Tout refuser » qui laisse passer la nouvelle finalité.
const ALL_ACCEPTED = Object.fromEntries(PURPOSES.map(({ id }) => [id, true]))
const ALL_REFUSED = Object.fromEntries(
  PURPOSES.map(({ id, required }) => [id, required === true]),
)

/**
 * Affiche le bandeau tant qu'aucun choix n'est enregistré, et le panneau de
 * réglages quand il est ouvert — depuis « Personnaliser » ou depuis le lien
 * permanent du pied de page.
 *
 * Le bandeau s'efface pendant que le panneau est ouvert : les deux proposent
 * les mêmes actions, les superposer donnerait deux « Tout accepter » à l'écran.
 */
export function ConsentGate() {
  const { needsChoice, isPanelOpen, openPanel, closePanel, setConsent, draft, setDraftPurpose } =
    useConsent()
  const bannerRef = useRef(null)

  // Ouvrir le panneau depuis le bandeau démonte le bandeau, donc le bouton
  // « Personnaliser » qui avait le focus. À la fermeture, le panneau ne peut
  // donc rien lui rendre et le focus retomberait sur <body> : le visiteur au
  // clavier devrait retraverser toute la page pour retrouver le choix. On note
  // ici qu'il faudra le reposer sur le bandeau réapparu.
  const focusBannerOnClose = useRef(false)

  useEffect(() => {
    if (isPanelOpen || !focusBannerOnClose.current) return
    focusBannerOnClose.current = false
    // Sans effet si le panneau s'est refermé sur un choix : le bandeau n'est
    // alors plus rendu, et il n'y a rien à refocaliser.
    bannerRef.current?.focus()
  }, [isPanelOpen])

  return (
    <>
      {needsChoice && !isPanelOpen && (
        <CookieBanner
          ref={bannerRef}
          onAcceptAll={() => setConsent(ALL_ACCEPTED)}
          onRejectAll={() => setConsent(ALL_REFUSED)}
          onCustomize={() => {
            focusBannerOnClose.current = true
            openPanel()
          }}
        >
          Nous déposons les cookies nécessaires au fonctionnement du site et à la réservation en
          ligne. Avec votre accord, nous mesurons aussi l'audience du site pour l'améliorer. Vous
          pouvez refuser en un clic, et changer d'avis à tout moment depuis le pied de page —{' '}
          <Link to="/cgu">en savoir plus</Link>.
        </CookieBanner>
      )}

      {isPanelOpen && (
        <ConsentPanel
          purposes={PURPOSES}
          value={draft}
          onToggle={setDraftPurpose}
          onSave={() => setConsent(draft)}
          onAcceptAll={() => setConsent(ALL_ACCEPTED)}
          onRejectAll={() => setConsent(ALL_REFUSED)}
          onClose={closePanel}
          intro="Vous choisissez finalité par finalité. Rien n'est déposé au titre d'une finalité optionnelle tant que vous ne l'avez pas cochée, et fermer cette fenêtre ne vaut pas acceptation."
          footer={
            <>
              Le détail des traceurs déposés — éditeur, durée, hébergement — figure à l'article 7
              des{' '}
              {/* La navigation laisserait sinon la fenêtre modale ouverte
                  par-dessus la page des CGU. */}
              <Link to="/cgu" onClick={closePanel}>
                CGU
              </Link>
              .
            </>
          }
        />
      )}
    </>
  )
}
